import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, CornerDownLeft, Loader2 } from "lucide-react";
import api, { LOGO_URL, BG_WELCOME, BG_FORM, BG_THANKS } from "../lib/api";
import { toast } from "sonner";

const KEYS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function BackgroundLayer({ src }) {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden">
      <img
        src={src}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        draggable="false"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
    </div>
  );
}

function TopBar({ progress, total, index }) {
  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-30 h-[2px] bg-zinc-900">
        <motion.div
          className="h-full bg-[color:var(--rivot-orange)]"
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="form-progress-bar"
        />
      </div>
      <header className="fixed top-0 left-0 right-0 z-20 px-6 md:px-12 pt-6 pointer-events-none">
        <div className="flex items-center justify-between">
          <img
            src={LOGO_URL}
            alt="RIVOT Motors"
            className="h-7 md:h-9 opacity-90"
            data-testid="rivot-logo"
          />
          {total > 0 && index >= 0 && (
            <div
              className="font-mono-rivot text-[11px] uppercase tracking-[0.25em] text-zinc-500"
              data-testid="form-step-counter"
            >
              {String(index + 1).padStart(2, "0")}
              <span className="text-zinc-700"> / {String(total).padStart(2, "0")}</span>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

function QuestionNumber({ n }) {
  return (
    <div className="font-mono-rivot text-xs uppercase tracking-[0.3em] text-[color:var(--rivot-orange)] mb-4">
      Q{String(n).padStart(2, "0")}
    </div>
  );
}

function ShortTextInput({ value, onChange, onEnter, field }) {
  const type =
    field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text";
  return (
    <input
      className="rivot-input"
      autoFocus
      type={type}
      placeholder="Type your answer here…"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onEnter();
        }
      }}
      data-testid={`input-${field.ref}`}
    />
  );
}

function LongTextInput({ value, onChange, onEnter, field }) {
  return (
    <textarea
      className="rivot-textarea"
      autoFocus
      placeholder="Type your answer here (Shift + Enter for new line)…"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onEnter();
        }
      }}
      data-testid={`input-${field.ref}`}
    />
  );
}

function ChoiceInput({ value, onChange, field, onEnter, options }) {
  const opts = options || field.choices;
  const selectedIdx = opts.indexOf(value);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const key = e.key.toUpperCase();
      const idx = KEYS.indexOf(key);
      if (idx >= 0 && idx < opts.length) {
        e.preventDefault();
        onChange(opts[idx]);
        setTimeout(() => onEnter(), 180);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = selectedIdx < 0 ? 0 : Math.min(opts.length - 1, selectedIdx + 1);
        onChange(opts[next]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const next = selectedIdx < 0 ? 0 : Math.max(0, selectedIdx - 1);
        onChange(opts[next]);
      } else if (e.key === "Enter" && selectedIdx >= 0) {
        e.preventDefault();
        onEnter();
      }
    };
    const node = containerRef.current;
    node?.addEventListener("keydown", handler);
    node?.focus();
    return () => node?.removeEventListener("keydown", handler);
  }, [selectedIdx, opts, onChange, onEnter]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className="grid gap-3 outline-none"
      data-testid={`choice-group-${field.ref}`}
    >
      {opts.map((opt, i) => (
        <button
          key={opt}
          type="button"
          className="rivot-choice"
          data-selected={value === opt}
          onClick={() => {
            onChange(opt);
            setTimeout(() => onEnter(), 160);
          }}
          data-testid={`choice-${field.ref}-${i}`}
        >
          <span className="rivot-choice-key">{KEYS[i]}</span>
          <span className="text-base md:text-lg">{opt}</span>
          {value === opt && (
            <Check size={18} className="ml-auto text-[color:var(--rivot-orange)]" />
          )}
        </button>
      ))}
    </div>
  );
}

function isAnswered(field, value) {
  if (!field.required) return true;
  if (field.type === "yes_no") return value === true || value === false;
  if (value === undefined || value === null) return false;
  if (typeof value === "string" && !value.trim()) return false;
  return true;
}

function validateField(field, value) {
  if (!isAnswered(field, value)) {
    return field.required ? "This field is required" : null;
  }
  if (field.type === "email" && value) {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!ok) return "Please enter a valid email address";
  }
  if (field.type === "phone" && value) {
    const digits = String(value).replace(/\D/g, "");
    if (digits.length < 7) return "Please enter a valid phone number";
  }
  return null;
}

export default function PublicForm() {
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(-1); // -1 = welcome, [0..n-1] = questions, n = thank you
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    api
      .get("/form/schema")
      .then((r) => setSchema(r.data))
      .catch(() => toast.error("Could not load the form. Please refresh."));
  }, []);

  const fields = useMemo(() => schema?.fields ?? [], [schema]);
  const total = fields.length;
  const currentField = step >= 0 && step < total ? fields[step] : null;
  const currentValue = currentField ? answers[currentField.ref] : undefined;
  const progress = total ? (step < 0 ? 0 : (step + 1) / (total + 1)) : 0;

  const setValue = useCallback(
    (v) => {
      if (!currentField) return;
      setAnswers((prev) => ({ ...prev, [currentField.ref]: v }));
    },
    [currentField]
  );

  const handleNext = useCallback(async () => {
    if (!currentField) return;
    const err = validateField(currentField, currentValue);
    if (err) {
      toast.error(err);
      return;
    }
    if (step === total - 1) {
      // submit
      setSubmitting(true);
      try {
        const payload = {
          answers: fields.map((f) => ({ ref: f.ref, value: answers[f.ref] ?? null })),
        };
        await api.post("/form/submit", payload);
        setDir(1);
        setStep(total);
        setSubmitted(true);
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || "Submission failed";
        toast.error(typeof msg === "string" ? msg : "Submission failed");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setDir(1);
    setStep((s) => s + 1);
  }, [currentField, currentValue, step, total, fields, answers]);

  const handleBack = useCallback(() => {
    if (step <= -1) return;
    setDir(-1);
    setStep((s) => s - 1);
  }, [step]);

  const bg = step === -1 ? BG_WELCOME : submitted ? BG_THANKS : BG_FORM;

  if (!schema) {
    return (
      <div className="relative min-h-screen">
        <BackgroundLayer src={BG_WELCOME} />
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="animate-spin text-zinc-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <BackgroundLayer src={bg} />
      <TopBar progress={progress} total={total} index={step} />

      <main className="relative min-h-screen flex items-center px-6 md:px-16 lg:px-24 pt-24 pb-16">
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait" custom={dir}>
            {step === -1 && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              >
                <div className="font-mono-rivot text-xs uppercase tracking-[0.35em] text-[color:var(--rivot-orange)] mb-6">
                  Dealer Partnership · India
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white">
                  {schema.welcome.title}
                </h1>
                <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed">
                  {schema.welcome.description}
                </p>
                <div className="mt-12 flex items-center gap-5">
                  <button
                    className="rivot-btn"
                    onClick={() => {
                      setDir(1);
                      setStep(0);
                    }}
                    data-testid="welcome-start-button"
                  >
                    {schema.welcome.button_text}
                    <ArrowRight size={18} />
                  </button>
                  <span className="text-xs text-zinc-500 font-mono-rivot uppercase tracking-widest">
                    Press Enter ↵
                  </span>
                </div>
                <div className="mt-16 text-xs text-zinc-600 font-mono-rivot uppercase tracking-[0.3em]">
                  {total} Questions · ~5 minutes
                </div>
              </motion.div>
            )}

            {step >= 0 && step < total && currentField && (
              <motion.div
                key={currentField.ref}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <QuestionNumber n={step + 1} />
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-tight">
                  {currentField.title}
                  {currentField.required && (
                    <span className="text-[color:var(--rivot-orange)] ml-2">*</span>
                  )}
                </h2>
                <div className="mt-10">
                  {currentField.type === "short_text" ||
                  currentField.type === "email" ||
                  currentField.type === "phone" ? (
                    <ShortTextInput
                      value={currentValue}
                      onChange={setValue}
                      onEnter={handleNext}
                      field={currentField}
                    />
                  ) : currentField.type === "long_text" ? (
                    <LongTextInput
                      value={currentValue}
                      onChange={setValue}
                      onEnter={handleNext}
                      field={currentField}
                    />
                  ) : currentField.type === "yes_no" ? (
                    <ChoiceInput
                      value={
                        currentValue === true
                          ? "Yes"
                          : currentValue === false
                          ? "No"
                          : undefined
                      }
                      onChange={(v) => setValue(v === "Yes")}
                      onEnter={handleNext}
                      field={currentField}
                      options={["Yes", "No"]}
                    />
                  ) : (
                    <ChoiceInput
                      value={currentValue}
                      onChange={setValue}
                      onEnter={handleNext}
                      field={currentField}
                    />
                  )}
                </div>

                <div className="mt-10 flex items-center gap-4 flex-wrap">
                  <button
                    className="rivot-btn"
                    disabled={submitting}
                    onClick={handleNext}
                    data-testid="form-next-button"
                  >
                    {submitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : step === total - 1 ? (
                      <>Submit <Check size={18} /></>
                    ) : (
                      <>OK <CornerDownLeft size={16} /></>
                    )}
                  </button>
                  <span className="text-[11px] text-zinc-500 font-mono-rivot uppercase tracking-widest">
                    Press <span className="text-zinc-300">Enter ↵</span>
                  </span>
                  {step > 0 && (
                    <button
                      className="ml-auto rivot-btn-ghost"
                      onClick={handleBack}
                      data-testid="form-back-button"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {step >= total && submitted && (
              <motion.div
                key="thanks"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <div className="font-mono-rivot text-xs uppercase tracking-[0.35em] text-[color:var(--rivot-orange)] mb-6">
                  Application Received
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white">
                  {schema.thank_you.title}
                </h1>
                <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg leading-relaxed">
                  {schema.thank_you.description}
                </p>
                <div className="mt-12 flex items-center gap-4">
                  <button
                    className="rivot-btn-ghost"
                    onClick={() => {
                      setAnswers({});
                      setSubmitted(false);
                      setStep(-1);
                    }}
                    data-testid="thanks-restart-button"
                  >
                    Submit another application
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <footer className="fixed bottom-4 right-6 z-20 font-mono-rivot text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        RIVOT Motors · Dealer Programme
      </footer>
    </div>
  );
}
